import React from 'react';
import Typography from '~/shared/components/Typography/Typography';
import styles from './BonusPointsHistory.module.scss';
import { Anchor } from '~/shared/components/Anchor';
import { useTranslation } from '~/shared/utils/translation';
import type { TranslationKey } from '~/lib/data-contract';
export interface PointsHistoryItem {
    id: string;
    type: 'purchase' | 'survey' | 'redeemed' | 'expired' | 'converted' | string;
    title: string;
    date: string;
    points: number;
    receipt?: string;
    pointsPending?: boolean;
    pendingDate?: string;
}

interface BonusPointsHistoryProps {
    items: PointsHistoryItem[];
    targetPoints?: number;
    rewardAmount?: number;
    daysAvailable?: number;
    className?: string;
}

const BonusPointsHistory: React.FC<BonusPointsHistoryProps> = ({
    items,
    targetPoints = 1500,
    rewardAmount = 25,
    daysAvailable = 30,
    className,
}) => {
    const { translate } = useTranslation();

    // Generate info text with interpolated values
    const infoText = translate('loyalty.pointsHistory.description', {
        targetPoints: targetPoints.toString(),
        rewardAmount: rewardAmount.toString(),
        daysAvailable: daysAvailable.toString(),
    });

    // Function to determine the display text for the points (either "Optjent" or "Konverteret")
    const getPointsDisplay = React.useCallback(
        (item: PointsHistoryItem) => {
            if (item.pointsPending) {
                const pendingDate = item.pendingDate || item.date;
                const parsedPendingDate = pendingDate ? new Date(pendingDate) : null;
                const isFutureDate = parsedPendingDate ? parsedPendingDate > new Date() : false;

                if (isFutureDate) {
                    const pendingMessage = translate('loyalty.pointsHistory.pendingInfo', {
                        points: Math.abs(item.points).toLocaleString('da-DK'),
                        date: item.pendingDate || item.date,
                    });

                    return (
                        <Typography
                            weight="medium"
                            variant="paragraph-S"
                            as="span"
                            className={styles.pointsPending}
                        >
                            {pendingMessage}
                        </Typography>
                    );
                }
            }

            if (item.type === 'converted') {
                return (
                    <>
                        <Typography
                            weight="medium"
                            variant="paragraph-S"
                            as="span"
                            className={styles.pointsConverted}
                        >
                            {`${translate('loyalty.converted')} ${Math.abs(item.points).toLocaleString('da-DK')} `}
                        </Typography>
                        <Typography variant="paragraph-S" as="span">
                            {translate('common.point')}
                        </Typography>
                    </>
                );
            }

            const prefix =
                item.points >= 0
                    ? `${translate('loyalty.earned')} +`
                    : `${translate('loyalty.earned')} `;
            return (
                <>
                    <Typography
                        weight="medium"
                        variant="paragraph-S"
                        as="span"
                        className={item.points >= 0 ? styles.pointsEarned : styles.pointsLost}
                    >
                        {`${prefix} ${Math.abs(item.points).toLocaleString('da-DK')} `}
                    </Typography>
                    <Typography variant="paragraph-S" as="span">
                        {translate('common.point')}
                    </Typography>
                </>
            );
        },
        [translate],
    );

    if (!items || items.length === 0) {
        return null;
    }

    return (
        <div className={`${styles.container} ${className || ''}`}>
            <Typography variant="title-S" as="h2" className={styles.title}>
                {translate('loyalty.pointsHistory.title')}
            </Typography>

            {infoText && <p className={styles.infoText}>{infoText}</p>}

            <div className={styles.historyList}>
                {items.map((item) => (
                    <div key={item.id} className={styles.historyItem}>
                        <div className={styles.itemDetails}>
                            <Typography
                                weight="medium"
                                variant="paragraph-S"
                                as="p"
                                className={styles.itemTitle}
                            >
                                {item.title}
                            </Typography>
                            <Typography variant="paragraph-S" as="p" className={styles.itemDate}>
                                {item.date}
                            </Typography>
                            {item.receipt && (
                                <Anchor
                                    href="/"
                                    variant="textLink-S"
                                    className={styles.receiptLink}
                                >
                                    {translate(item.receipt as TranslationKey)}
                                </Anchor>
                            )}
                        </div>
                        <div className={styles.pointsAmount}>{getPointsDisplay(item)}</div>
                    </div>
                ))}
            </div>
        </div>
    );
};

export default BonusPointsHistory;
